import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { DEFAULT_COMPACT_THRESHOLD, formatPrice } from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";

export interface BaselineLineProps {
  readonly seriesId: string;
  readonly currency?: string;
  readonly hideYAxisLabel?: boolean;
}

const LABEL_PADDING_X = 4;
const LABEL_PADDING_Y = 3;

const BaselineLineInner: React.FC<BaselineLineProps> = ({
  seriesId,
  currency,
  hideYAxisLabel = false,
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["scales", "data"]);

  const content = useMemo(() => {
    const currentStore = storeRef.current;
    const state = currentStore.getState();
    const scales = currentStore.getScales();
    const layout = currentStore.getLayout();
    const visibleData = currentStore.getVisibleData(seriesId);
    const seriesMeta = state.seriesMeta.get(seriesId);

    const { baseValue } = state;

    if (visibleData.length === 0) return null;

    const baselineValue = baseValue;

    const y = scales.y.scale(baselineValue);
    const { priceArea, typography } = layout;

    if (y < priceArea.y || y > priceArea.y + priceArea.height) return null;

    const x1 = priceArea.x;
    const x2 = priceArea.x + priceArea.width;

    const seriesCurrency = currency ?? seriesMeta?.issuedCurrency ?? "USD";
    const [yMin, yMax] = scales.y.domain();
    const useCompact =
      Math.abs(yMax) >= DEFAULT_COMPACT_THRESHOLD ||
      Math.abs(yMin) >= DEFAULT_COMPACT_THRESHOLD;
    const label = formatPrice(baselineValue, {
      currency: seriesCurrency,
      compact: useCompact,
    });

    const labelWidth =
      label.length * typography.charWidth + LABEL_PADDING_X * 4;
    const labelHeight = typography.axisLabelSize + LABEL_PADDING_Y * 2;

    const yAxisOnRight = layout.yAxisArea.x > priceArea.x;
    const labelX = yAxisOnRight ? x2 : priceArea.x - labelWidth;
    const labelY = y - labelHeight / 2;

    return {
      y,
      x1,
      x2,
      label,
      labelX,
      labelY,
      labelWidth,
      labelHeight,
      fontSize: typography.axisLabelSize,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, revision, currency]);

  if (!content) return null;

  const {
    y,
    x1,
    x2,
    label,
    labelX,
    labelY,
    labelWidth,
    labelHeight,
    fontSize,
  } = content;

  const textX = labelX + LABEL_PADDING_X;

  return (
    <g
      className={styles.baselineLine}
      data-component="price-movement-chart-baseline"
    >
      <line
        className={styles.baselineLineStroke}
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        data-component="price-movement-chart-baseline-line"
      />
      {!hideYAxisLabel && (
        <g data-component="price-movement-chart-baseline-label">
          <rect
            className={styles.baselineLabelBg}
            x={labelX}
            y={labelY}
            width={labelWidth}
            height={labelHeight}
            rx={2}
            ry={2}
          />
          <text
            className={styles.baselineLabelText}
            dy="0.32em"
            fontSize={fontSize}
            textAnchor="start"
            x={textX}
            y={y}
          >
            {label}
          </text>
        </g>
      )}
    </g>
  );
};

export const BaselineLine = React.memo(BaselineLineInner);
BaselineLine.displayName = "BaselineLine";
